import React, { useState } from 'react';
import { DocumentItem, LanguageCode } from '../types';
import { translations } from '../i18n/translations';
import { getLocalized, getLocalizedCategory } from '../utils/i18n';
import { FileText, Download, Search } from 'lucide-react';
import { downloadDocumentApi } from '../services/api';

interface DocumentsViewProps {
  currentLang: LanguageCode;
  documents: DocumentItem[];
}

export const DocumentsView: React.FC<DocumentsViewProps> = ({ currentLang, documents }) => {
  const t = translations[currentLang];
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('Tüm');

  const rawCategories = ['Tüm', 'Tüzük', 'Faaliyet Raporu', 'Formlar'];

  const filtered = documents.filter((doc) => {
    const matchesCat = selectedCategory === 'Tüm' || doc.category === selectedCategory;
    const titleText = getLocalized(doc.title, currentLang);
    const matchesSearch = titleText.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCat && matchesSearch;
  });

  const handleDownload = async (doc: DocumentItem) => {
    await downloadDocumentApi(doc.id);
    if (doc.fileUrl && doc.fileUrl.startsWith('http')) {
      window.open(doc.fileUrl, '_blank');
    } else {
      alert(`"${getLocalized(doc.title, currentLang)}" (${doc.fileSize}) belgesi veritabanı üzerinden indirildi.`);
    }
  };

  return (
    <div className="space-y-8 pb-12">
      {/* Header */}
      <div className="text-center max-w-3xl mx-auto space-y-3">
        <span className="px-3.5 py-1 rounded-full bg-[#132238] text-[#38BDF8] text-xs font-extrabold uppercase tracking-wider border border-[#38BDF8]/40 shadow-[0_0_15px_rgba(56,189,248,0.2)]">
          {t.documentsBadge}
        </span>
        <h1 className="text-3xl sm:text-5xl font-extrabold text-white font-heading">
          {t.documentsMainTitle}
        </h1>
        <p className="text-sm text-gray-300">
          {t.documentsSubtitle}
        </p>
      </div>

      {/* Search & Category Filter */}
      <div className="bg-[#132238] rounded-2xl p-4 border-2 border-[#38BDF8]/30 flex flex-col md:flex-row items-center justify-between gap-4 shadow-xl">
        <div className="flex items-center gap-2 overflow-x-auto w-full md:w-auto pb-2 md:pb-0 scrollbar-none">
          {rawCategories.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all ${
                selectedCategory === cat
                  ? 'bg-[#38BDF8] text-[#0B1426] shadow-[0_0_12px_rgba(56,189,248,0.6)]'
                  : 'bg-[#0B1426] text-gray-300 hover:text-white border border-[#38BDF8]/20'
              }`}
            >
              {getLocalizedCategory(cat, currentLang)}
            </button>
          ))}
        </div>

        <div className="relative w-full md:w-64">
          <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-[#38BDF8]" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t.searchDocuments}
            className="w-full pl-10 pr-4 py-2 rounded-xl bg-[#0B1426] border border-[#38BDF8]/30 text-white text-xs placeholder-gray-400 focus:outline-none focus:border-[#38BDF8]"
          />
        </div>
      </div>

      {/* Documents List */}
      <div className="space-y-4">
        {filtered.map((doc) => (
          <div
            key={doc.id}
            className="bg-[#132238] rounded-2xl p-5 border border-[#38BDF8]/30 flex flex-col sm:flex-row items-center justify-between gap-4 hover:border-[#38BDF8] hover:shadow-[0_0_20px_rgba(56,189,248,0.25)] transition-all group"
          >
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-[#0B1426] text-[#38BDF8] border border-[#38BDF8]/40 flex items-center justify-center flex-shrink-0 group-hover:bg-[#38BDF8] group-hover:text-[#0B1426] transition-colors shadow-md">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <span className="px-2.5 py-0.5 rounded-md bg-[#0B1426] text-[#38BDF8] text-[10px] font-extrabold border border-[#38BDF8]/30">
                  {getLocalizedCategory(doc.category, currentLang)}
                </span>
                <h3 className="text-sm font-bold text-white mt-1 group-hover:text-[#38BDF8] transition-colors">
                  {getLocalized(doc.title, currentLang)}
                </h3>
                <p className="text-[11px] text-cyan-200/80 mt-1">
                  📅 {doc.date} • {doc.fileSize}
                </p>
              </div>
            </div>

            <button
              onClick={() => handleDownload(doc)}
              className="px-4 py-2.5 rounded-xl bg-[#38BDF8] text-[#0B1426] font-extrabold text-xs hover:bg-[#A5F3FC] hover:shadow-[0_0_15px_rgba(165,243,252,0.8)] transition-all flex items-center justify-center gap-2 flex-shrink-0 w-full sm:w-auto shadow-md"
            >
              <Download className="w-4 h-4" />
              <span>{t.downloadDoc}</span>
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};
